import Vue from 'vue'
import { normalize } from 'normalizr'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { patientSchema } from '@/schema/patient'
import { PatientEntities } from '@/schema/patient/types'
import { Patient } from '@/types/user'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  patients: {},
  sort: [],
  totalCount: 0,
  currentPatient: null
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  patients: state => state.patients,
  sort: state => (Array.isArray(state.sort) ? state.sort : []),
  totalCount: state => state.totalCount,
  currentPatient: state => state.currentPatient
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  clear: state => {
    state.patients = {}
    state.sort = []
    state.totalCount = 0
  },

  update: (state, { id, patient }) => {
    Vue.set(state.patients, id, patient)
  },

  updateList: (state, { patients, sort = [], totalCount = undefined }) => {
    state.totalCount = totalCount
    state.patients = patients
    state.sort = sort
  },

  add: (state, { patients, sort = undefined, toAppend = true }) => {
    if (sort === undefined) {
      sort = Object.keys(patients).map(id => Number(id))
    }

    sort = sort.filter(
      id => !state.patients || state.patients[id] === undefined
    )

    state.sort = toAppend
      ? (state.sort || []).concat(sort)
      : sort.concat(state.sort || [])

    state.patients = Object.assign({}, state.patients, patients)
  },

  setCurrentPatient: (state, patient) => {
    state.currentPatient = patient
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async update({ commit }, { patientId }) {
    const res = await Vue.$api.user.patient({ patientId })
    const { entities } = normalize<Patient, PatientEntities, number>(
      res,
      patientSchema
    )

    commit('update', {
      patient: entities.patients[patientId],
      id: patientId
    })
  },

  async updateList({ commit }, { page, query, scope, archived }) {
    const res = await Vue.$api.user.patientList({
      page,
      query,
      scope,
      archived
    })
    const { result, entities } = normalize<
      Patient[],
      PatientEntities,
      number[]
    >(res.results, [patientSchema])

    const { patients } = entities

    commit('updateList', {
      totalCount: res.count,
      sort: result,
      patients
    })
  },

  async addOne({ commit }, { patientId, toAppend = true }) {
    const res = await Vue.$api.user.patient({ patientId })
    const { entities } = normalize<Patient, PatientEntities, number>(
      res,
      patientSchema
    )

    commit('add', {
      patients: entities.patients,
      sort: [patientId],
      toAppend
    })
  },

  async updateCurrentPatient({ commit }, { patientId }) {
    if (patientId == null) {
      commit('setCurrentPatient', null)
    } else {
      const patient = await Vue.$api.user.patient({ patientId })
      commit('setCurrentPatient', patient)
    }
  },

  async patchCurrentPatient({ commit }, { patientId, params }) {
    const patient = await Vue.$api.user.patchPatient({ patientId, params })
    commit('setCurrentPatient', patient)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
